import { DatePicker } from "@app/components/DatePicker.tsx";
import { PerformanceModel } from "@app/data/engine.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { staffPerformanceTableDefinition } from "@app/table-definitions/staff-performance.table-definition.tsx";
import { Box, Button, Grid2 } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { CurrencyInput, DataTable } from "bi-lib";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

export default function PerformanceForm({ id, setOnSave }: Props) {
    const [selectedPerformance, setSelectedPerformance] = useState<PerformanceModel>();

    const { savePerformance } = useStaffData();

    const defaultValues: any = {
         effectiveDate: selectedPerformance?.effectiveDate ? null : moment(selectedPerformance?.effectiveDate),
            targetBillingRate: selectedPerformance?.targetBillingRate,
            targetUtilizationRate: selectedPerformance?.targetUtilizationRate,
    };
    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) =>
            await savePerformance.mutateAsync({
                staffId: id,
                performanceId: selectedPerformance?.performanceId,
                effectiveDate: value.effectiveDate?.format("MM/DD/YYYY"),
                targetBillingRate: value.targetBillingRate?.toString(),
                targetUtilizationRate: value.targetUtilizationRate?.toString(),
            }),
    });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (ref.current?.reportValidity()) {
            await form.handleSubmit();
            return;
        }
        throw new Error("Form is invalid");
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, [form]);

    return (
        <>
            <Box mb={3}>
                <PerformanceList id={id} setSelectedPerformance={setSelectedPerformance} />
            </Box>
            <form
                ref={ref}
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Grid2 container spacing={1}>
                    <Grid2 size={4}>
                        <form.Field name={"effectiveDate"}>
                            {(field) => (
                                <DatePicker
                                    label={"Effective Date"}
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e)}
                                    sx={{ width: "100%" }}
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"targetUtilizationRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Target Utilization Rate"}
                                    value={field.state.value ?? ""}
                                    type={"number"}
                                    onChange={(e) => field.handleChange(e)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"targetBillingRate"}>
                            {(field) => (
                                <CurrencyInput
                                    label={"Target Billing Rate"}
                                    value={field.state.value ?? ""}
                                    type={"number"}
                                    onChange={(e) => field.handleChange(e)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                </Grid2>
                <Box mt={1}>
                    <Button variant={"outlined"} onClick={() => setSelectedPerformance(undefined)} fullWidth>
                        Clear
                    </Button>
                </Box>
            </form>
        </>
    );
}

interface ListProps {
    id?: number;
    setSelectedPerformance: (performance: PerformanceModel) => void;
}

function PerformanceList({ id, setSelectedPerformance }: ListProps) {
    const { staffPerformance, deletePerformance } = useStaffData({ id });

    return (
        <DataTable
            rowsPerPageOptions={[5000]}
            label={"Staff Performance"}
            columns={staffPerformanceTableDefinition({
                onEdit: setSelectedPerformance,
                onDelete: deletePerformance.mutate,
                rowCount: staffPerformance.data?.length ?? 0,
            })}
            data={staffPerformance.data ?? []}
            queryHook={staffPerformance}
        />
    );
}
